import { Injectable } from '@angular/core';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import {
  GeneralIndex,
  AcIndex,
  LcIndex,
  FQIndex,
  UDIndex,
} from './calculation.const';
import {
  OriginalData,
  PerCapitaBaselineCalculation,
  UserDefinedData,
} from './Interface/calculation-models';
 
@Injectable({
  providedIn: 'root',
})
export class PerCapitaBaselineCalculationService
  implements PerCapitaBaselineCalculation
{
  private totalSourceAllocation = 0;
  private unitValueToAdjust = 0;
  private originalData: OriginalData;
  private userDefinedData: UserDefinedData;
  private type = '';
  private allColumnsLength = 0;
  private oldPPPMap: Map<number, number> = new Map();
  private userDefinedScenarioData: any;
  private saPrecomputedUnits: Map<number, number> | null = null;
 
  constructor(private utilitiesService: UtilitiesService) {}
 
  public convertPercentageToCount(
    originalData: OriginalData,
    userDefinedData: UserDefinedData,
    dealType: string,
    userDefinedScenarioData?: any
  ): OriginalData {
    const peakPatientCountData = this.calculatePeakPatientCountData(
      originalData,
      userDefinedData,
      dealType,
      userDefinedScenarioData,
      true,
    );
    return peakPatientCountData;
  }
 
  public calculatePeakPatientCountData(
    originalData: OriginalData,
    userDefinedData: UserDefinedData,
    type: string,
    userDefinedScenarioData?: any,
    isPPP?: boolean
  ): OriginalData {
    /** DO NOT CHANGE THE ORDER OF THE FUNCTION CALLING */
    this.resetVariables(originalData, userDefinedData, type, userDefinedScenarioData);
 
      this.userDefinedScenarioData.forEach((element, index) => {
        if(element.base.IsCompanyProduct === true){
          this.unitValueToAdjust = 0;
          this.totalSourceAllocation = 0;
          this.getSourceAllocation();
          if (isPPP) {
                /** Convert the percentage into count */
                this.calculatePPC(index);
              }
          this.calculateParameters(index);
          if (this.unitValueToAdjust > 0) {
            this.adjustClientUnit(index);
            this.clampNegativeCompetitorUnits();
            this.adjustPPC(index);
          }
        }
    });
    this.calculateMPI(userDefinedScenarioData);
    this.adjustClientTrx();
    this.calculateTrxPercentage();
    if (isPPP) {
      /** Convert the count into percentage before returning */
      this.calculatePercentage();
    }
    return this.originalData;
  }
 
  private getSourceAllocation() {
    for (let i = 0; i < this.allColumnsLength; i++) {
      const currentSourceAllocation = this.utilitiesService.ifNullThenZero(
        this.userDefinedData[i][GeneralIndex.sa]
      );
      this.totalSourceAllocation += currentSourceAllocation;
    }
  }
 
  private calculateTrxPercentage() {
    const totalTrx = this.getTotalTrx();
    this.originalData.tableData[this.type].allColumns.forEach(
      (dataRow, index, array) => {
        dataRow.forEach((dataObj, columnIndex) => {
          if (columnIndex === AcIndex.percentage) {
            array[index][AcIndex.percentage].value =
              (array[index][AcIndex.trx].value / totalTrx) * 100;
          }
        });
      }
    );
  }
 
  private getTotalTrx() {
    let totalTrx = 0;
    this.originalData.tableData[this.type].allColumns.forEach(
      (dataRow, index, array) => {
        dataRow.forEach((dataObj, columnIndex) => {
          if (columnIndex === AcIndex.trx) {
            totalTrx += array[index][AcIndex.trx].value;
          }
        });
      }
    );
    return totalTrx;
  }
 
  private calculateParameters(clientIndex: number) {
    this.saPrecomputedUnits = null;
    if (this.totalSourceAllocation !== 0) {
      this.saPrecomputedUnits = this.computeSACompetitorUnitsIterative(clientIndex);
    }
    this.originalData.tableData[this.type].allColumns.map(
      (dataRow, prdIdx) => {
        dataRow.map((dataObj, columnIndex, array) => {
          this.calculateData(
            columnIndex,
            prdIdx,
            array,
            prdIdx,
            prdIdx === clientIndex,
            clientIndex
          );
        });
      }
    );
  }
 
  private calculateData(
    columnIndex: number,
    prdIdx: number,
    array: any[],
    baselineRowIndex: number,
    isClient: boolean,
    clientIndex: number,
  ) {
    switch (columnIndex) {
      case AcIndex.ppc:
        const peakPatientCount = this.userDefinedData[prdIdx][UDIndex.ppc];
        array[AcIndex.ppc].value = peakPatientCount;
        break;
      case AcIndex.units:
        const isClientPrd = this.userDefinedScenarioData[prdIdx].base.IsCompanyProduct;
        if(!isClient && isClientPrd){
          break;
        }
        const unitValue = isClient
          ? this.calculateUnit(baselineRowIndex)
          : this.calculateCompetitorUnit(baselineRowIndex,clientIndex);
        if (unitValue > 0) {
          array[AcIndex.units].value =
            this.utilitiesService.ifNullThenZero(unitValue);
        } else {
          array[AcIndex.units].value =
            this.utilitiesService.ifNullThenZero(unitValue);
          this.unitValueToAdjust += Math.abs(unitValue);
        }
        break;
      case AcIndex.sa:
        const sourceAllocation = this.userDefinedData[prdIdx][LcIndex.sa];
        array[AcIndex.sa].value = sourceAllocation;
        break;
      case AcIndex.mpi:
        const multiProductImpact = this.userDefinedData[prdIdx][LcIndex.mpi];
        array[AcIndex.mpi].value = multiProductImpact;
        break;
    }
  }
 
  private calculateUnit(i: number): number {
    const peakPatientCount = this.userDefinedData[i][UDIndex.ppc];
    const livesToUnitsFactor =
      this.originalData.finalQuarterData.data[i][FQIndex.livesToUnits].value;
    const scalingFactor =
      this.originalData.finalQuarterData.data[i][FQIndex.scalingFactor].value;
    const returnValue =
      this.utilitiesService.ifNullThenZero(peakPatientCount * scalingFactor *
      livesToUnitsFactor);
    return returnValue;
  }
 
  private calculateCompetitorUnit(i: number,prdIdx: number): number {
    let patientCount : number;
    if(prdIdx === GeneralIndex.client){
      const baselinePatientCount = this.originalData.finalQuarterData.data[i][FQIndex.unit].value;
      patientCount = baselinePatientCount;
    }else{
      patientCount = this.originalData.tableData[this.type].allColumns[i][AcIndex.units].value;
    }
 
    const scaledUnits = this.originalData.finalQuarterData.data[prdIdx][FQIndex.unit].value;
   
    const numberOfCompetitor = this.getNumberOfCompetitors();
   
    const ppcUnits = this.originalData.tableData[this.type].allColumns[prdIdx][AcIndex.units].value;
   
    const sourceAllocation = this.utilitiesService.ifNullThenZero(this.userDefinedData[i][LcIndex.sa]);
   
    if (this.totalSourceAllocation === 0) {
      // Fair share: distribute proportionally to each competitor's current volume.
      // For the first brand pass (prdIdx === 0) use FQ baseline;
      // for subsequent brand passes use the current allColumns state so that
      // already-exhausted competitors are not asked to give more than they have.
      const isFirstBrandPass = (prdIdx === GeneralIndex.client);
      const competitorCurrentUnits = isFirstBrandPass
        ? this.originalData.finalQuarterData.data[i][FQIndex.unit].value
        : this.originalData.tableData[this.type].allColumns[i][AcIndex.units].value;
      const totalCompetitorUnits = isFirstBrandPass
        ? this.getTotalCompetitorBaselineUnits()
        : this.getTotalCurrentCompetitorUnits();
      if (totalCompetitorUnits === 0) {
        return patientCount - (ppcUnits - scaledUnits) * (100 / numberOfCompetitor / 100);
      }
      const fairShareFraction = competitorCurrentUnits / totalCompetitorUnits;
      return patientCount - (ppcUnits - scaledUnits) * fairShareFraction;
    }
    // SA-weighted path: use iteratively-precomputed result (Pass 1 SA%, Pass 2+ fair share)
    if (this.saPrecomputedUnits !== null && this.saPrecomputedUnits.has(i)) {
      return this.saPrecomputedUnits.get(i)!;
    }
    // Fallback: simple formula when clientDelta ≤ 0 (competitor gains — no exhaustion risk)
    return patientCount - (ppcUnits - scaledUnits) * (sourceAllocation / 100);
  }

  /**
   * Iteratively computes final units for every SA-weighted competitor after the client
   * absorbs its PPC-driven gain.
   *
   * Pass 1 – SA-weighted: each competitor gives min(clientDelta × SA%, availableUnits).
   *           Any gap (exhausted competitor) becomes shortfall.
   * Pass 2+ – fair share: shortfall is redistributed proportionally to remaining volume
   *            among ALL non-exhausted competitors (regardless of SA%) until shortfall ≈ 0
   *            or the market is fully drained.
   * Truly uncoverable shortfall → unitValueToAdjust (caps the client's gain).
   *
   * Returns null when clientDelta ≤ 0 (competitors gain — simple per-row formula used).
   */
  private computeSACompetitorUnitsIterative(clientIndex: number): Map<number, number> | null {
    const ppcUnits = this.calculateUnit(clientIndex);
    const scaledUnits = this.originalData.finalQuarterData.data[clientIndex][FQIndex.unit].value;
    const clientDelta = ppcUnits - scaledUnits;

    if (clientDelta <= 0) {
      return null;
    }

    const isFirstBrandPass = (clientIndex === GeneralIndex.client);
    const competitors: { index: number; saFraction: number; available: number }[] = [];

    this.userDefinedScenarioData.forEach((element, i) => {
      if (element.base.IsCompanyProduct === false) {
        const sa = this.utilitiesService.ifNullThenZero(this.userDefinedData[i][LcIndex.sa]);
        const currentUnits = isFirstBrandPass
          ? this.originalData.finalQuarterData.data[i][FQIndex.unit].value
          : this.originalData.tableData[this.type].allColumns[i][AcIndex.units].value;
        competitors.push({
          index: i,
          saFraction: sa / 100,
          available: Math.max(0, currentUnits),
        });
      }
    });

    // Pass 1: SA-weighted extraction — cap each competitor at their available units.
    // Competitors that cannot fulfil their full SA% share produce a shortfall.
    let shortfall = 0;
    for (const c of competitors) {
      const requested = clientDelta * c.saFraction;
      const actual = Math.min(Math.max(0, requested), c.available);
      c.available -= actual;
      shortfall += Math.max(0, requested - actual);
    }

    // Pass 2+: redistribute shortfall via fair share (proportional to remaining volume)
    // among ALL non-exhausted competitors, regardless of their original SA%.
    // Repeat until shortfall ≈ 0 or every competitor is drained.
    const maxPasses = competitors.length + 1;
    let passCount = 0;
    while (shortfall > 1e-6 && passCount++ < maxPasses) {
      const eligible = competitors.filter(c => c.available > 1e-6);
      if (eligible.length === 0) { break; }
      const totalAvailable = eligible.reduce((sum, c) => sum + c.available, 0);
      if (totalAvailable <= 1e-6) { break; }
      let newShortfall = 0;
      for (const c of eligible) {
        const fraction = c.available / totalAvailable;
        const requested = shortfall * fraction;
        const actual = Math.min(requested, c.available);
        c.available -= actual;
        newShortfall += requested - actual;
      }
      shortfall = newShortfall;
    }

    // Any truly uncoverable shortfall (market fully drained) reduces the client's gain.
    if (shortfall > 1e-6) {
      this.unitValueToAdjust += shortfall;
    }

    // Build result: competitor index → remaining units (never negative)
    const result = new Map<number, number>();
    for (const c of competitors) {
      result.set(c.index, c.available);
    }
    return result;
  }
 
  private calculateMPI(userDefinedScenarioData?: any) {
    let updatedClientUnit = 0;
    let finalQuarterBaselineClientUnits = 0;
    this.originalData.tableData[this.type].allColumns.forEach(
      (dataRow, index) => {
        if (userDefinedScenarioData[index].base.IsCompanyProduct === true) {
          updatedClientUnit += dataRow[AcIndex.units].value;          
          finalQuarterBaselineClientUnits += this.originalData.finalQuarterData.data[index][FQIndex.unit].value
        } else {
          const updatedCompetitorUnits = dataRow[AcIndex.units].value;
 
          const multiProductImpact = this.utilitiesService.ifNullThenZero(this.userDefinedData[index][LcIndex.mpi]);
 
          const competitorMultiProductImpactUnit = updatedCompetitorUnits +
            (updatedClientUnit - finalQuarterBaselineClientUnits) * (multiProductImpact / 100);
 
          dataRow[AcIndex.units].value = this.utilitiesService.ifNullThenZero(competitorMultiProductImpactUnit);
 
          if (competitorMultiProductImpactUnit < 0) {
            this.adjustMultiProductImpact(
              dataRow,
              index,
              this.utilitiesService.ifNullThenZero(competitorMultiProductImpactUnit),
              updatedClientUnit,
              updatedCompetitorUnits,
              finalQuarterBaselineClientUnits
            );
          }
        }
      }
    );
  }

  private adjustMultiProductImpact(
    dataRow,
    index,
    CompetitorMultiProductImpactUnit,
    updatedClientUnit,
    updatedCompetitorUnits,
    finalQuarterBaselineClientUnits
  ) {

    const updatedMultiProductImpact = ((CompetitorMultiProductImpactUnit - updatedCompetitorUnits) /
        (updatedClientUnit - finalQuarterBaselineClientUnits)) * 100;

    const oldMpi = dataRow[AcIndex.mpi].value;

    dataRow[AcIndex.mpi].value = this.utilitiesService.forPercentage(updatedMultiProductImpact);

    this.originalData.tableData[this.type].allColumns[index][AcIndex.mpi].value = dataRow[AcIndex.mpi].value;
    this.originalData.tableData[this.type].allColumns[index][AcIndex.mpi].highlight = !(dataRow[AcIndex.mpi].value === oldMpi);

    dataRow[AcIndex.mpi].highlight = !(dataRow[AcIndex.mpi].value === oldMpi);
  }

  private getNumberOfCompetitors() {
    // implement the function to find the number competitor using competitor flag
    return this.userDefinedScenarioData.filter((element) => element.base.IsCompanyProduct === false).length;
  }
 
  private getTotalCompetitorBaselineUnits(): number {
    let total = 0;
    this.userDefinedScenarioData.forEach((element, index) => {
      if (element.base.IsCompanyProduct === false) {
        total += this.originalData.finalQuarterData.data[index][FQIndex.unit].value;
      }
    });
    return total;
  }

  private getTotalCurrentCompetitorUnits(): number {
    let total = 0;
    this.userDefinedScenarioData.forEach((element, index) => {
      if (element.base.IsCompanyProduct === false) {
        total += this.originalData.tableData[this.type].allColumns[index][AcIndex.units].value;
      }
    });
    return total;
  }

  private clampNegativeCompetitorUnits(): void {
    this.userDefinedScenarioData.forEach((element, i) => {
      if (element.base.IsCompanyProduct === false) {
        const units = this.originalData.tableData[this.type].allColumns[i][AcIndex.units].value;
        if (units < 0) {
          this.originalData.tableData[this.type].allColumns[i][AcIndex.units].value = 0;
        }
      }
    });
  }
 
  private adjustClientUnit(clientIndex: number) {
    const clientUnitAdjusted = this.utilitiesService.ifNullThenZero(
      this.originalData.tableData[this.type].allColumns[clientIndex][
        AcIndex.units
      ].value - this.unitValueToAdjust
    );
    this.originalData.tableData[this.type].allColumns[clientIndex][
      AcIndex.units
    ].value = clientUnitAdjusted;
  }
 
  private adjustPPC(clientIndex: number) {
    const oldPPC =
      this.originalData.tableData[this.type].limitedColumns[
        clientIndex
      ][LcIndex.ppc].value;
    const clientUnits =
      this.originalData.tableData[this.type].allColumns[clientIndex][
        AcIndex.units
      ].value;
    const livesToUnits =
      this.originalData.finalQuarterData.data[clientIndex][
        FQIndex.livesToUnits
      ].value;
    const scalingFactor =
      this.originalData.finalQuarterData.data[clientIndex][
        FQIndex.scalingFactor
      ].value;
    const updatedPpc = this.utilitiesService.ifNullThenZero(
      clientUnits / (livesToUnits * scalingFactor)
    );
    this.userDefinedData[clientIndex][UDIndex.ppc] = updatedPpc;
    this.originalData.tableData[this.type].allColumns[clientIndex][
      AcIndex.ppc
    ].value = updatedPpc;
    this.originalData.tableData[this.type].limitedColumns[clientIndex][
      LcIndex.ppc
    ].highlight = !(oldPPC === updatedPpc);
    this.originalData.tableData[this.type].allColumns[clientIndex][
      AcIndex.ppc
    ].highlight = !(oldPPC === updatedPpc);
  }
 
  private adjustClientTrx() {
    for (let i = 0; i < this.allColumnsLength; i++) {
      const units: number =
        this.originalData.tableData[this.type].allColumns[i][AcIndex.units]
          .value;
      const unitToTrxFactor: number =
        this.originalData.finalQuarterData.data[i][FQIndex.unitToTrx].value;
      const trx = this.utilitiesService.ifNullThenZero(units / unitToTrxFactor);
      this.originalData.tableData[this.type].allColumns[i][AcIndex.trx].value =
        trx;
    }
  }
 
  private resetVariables(
    originalData: OriginalData,
    userDefinedData: UserDefinedData,
    type: string,
    userDefinedScenarioData?: any
  ) {
    this.totalSourceAllocation = 0;
    this.unitValueToAdjust = 0;
    this.saPrecomputedUnits = null;
    this.originalData = originalData;
    this.userDefinedData = userDefinedData;
    this.userDefinedScenarioData = userDefinedScenarioData;
    this.type = type;
    this.allColumnsLength =
      this.originalData.tableData.baseline.allColumns.length;
    // Save each company brand's PPP before calculatePPC overwrites userDefinedData with PPC
    // and reset highlights for all company brands
    this.oldPPPMap = new Map();
    this.userDefinedScenarioData.forEach((element, idx) => {
      if (element.base.IsCompanyProduct === true) {
        this.oldPPPMap.set(idx, Number(this.userDefinedData[idx][UDIndex.ppp]));
        this.originalData.tableData[this.type].allColumns[idx][AcIndex.ppc].highlight = false;
        this.originalData.tableData[this.type].limitedColumns[idx][LcIndex.ppc].highlight = false;
      }
    });
  }
 
  private calculatePPC(clientIndex : number) {
    const peakPatientPercentage =
      this.userDefinedData[clientIndex][UDIndex.ppp];
    // change the unit index to patient count index
    const finalQuarterBaselinePatientCount =
      this.originalData.finalQuarterData.data[clientIndex][
        FQIndex.patient
      ].value;
     
    // Peak patient count = (1+ (Peak patient percentage / 100)) * final quarter baseline units
    const peakPatientCount =
      (1 + peakPatientPercentage / 100) * finalQuarterBaselinePatientCount;
    this.userDefinedData[clientIndex][UDIndex.ppc] = peakPatientCount;
    this.originalData.tableData[this.type].limitedColumns[clientIndex][
      LcIndex.ppc
    ].highlight = true;
 
 
    // Per-quarter PPC: PPP (%) * scaled units of that quarter
    const clientQuarterRow = this.originalData.units[this.type].tableData[clientIndex];
 
    const impactForEveryQuarter = clientQuarterRow.map((quarterCell, quarterIndex) => {
      const scaledUnits = this.utilitiesService.ifNullThenZero(quarterCell.value);
      const quarter = this.originalData.units[this.type].heading[quarterIndex];
      return {
        quarter,
        impact: (peakPatientPercentage / 100) * scaledUnits,
      };
    });
 
    if (!this.originalData.impact) {
      this.originalData.impact = {};
    }
    if (!this.originalData.impact[this.type]) {
      this.originalData.impact[this.type] = {};
    }
 
    if (!this.originalData.impact[this.type].ppcPerQuarter) {
      this.originalData.impact[this.type].ppcPerQuarter = {};
    }
    this.originalData.impact[this.type].ppcPerQuarter[clientIndex] = impactForEveryQuarter;
  }
 
  private calculatePercentage() {
    this.userDefinedScenarioData.forEach((element, clientIndex) => {
      if (element.base.IsCompanyProduct !== true) { return; }
      const peakPatientCount = this.userDefinedData[clientIndex][UDIndex.ppc];
      const finalQuarterBaselinePatientCount =
        this.originalData.finalQuarterData.data[clientIndex][FQIndex.patient].value;
      let peakPatientPercentage = 0;
      try {
        peakPatientPercentage = this.utilitiesService.forPercentage(
          (peakPatientCount / finalQuarterBaselinePatientCount - 1) * 100
        );
      } catch (error) {
        peakPatientPercentage = 0;
      }
      this.updatePPP(peakPatientPercentage, clientIndex);
    });
  }

  private updatePPP(peakPatientPercentage: number, clientIndex: number) {
    const oldPPP = this.oldPPPMap.get(clientIndex) ?? 0;
    if (oldPPP < 0) {
      peakPatientPercentage = oldPPP;
    }
    this.originalData.tableData[this.type].allColumns[clientIndex][
      AcIndex.ppc
    ].value = peakPatientPercentage;
    this.originalData.tableData[this.type].allColumns[clientIndex][
      AcIndex.ppc
    ].highlight = !(oldPPP === peakPatientPercentage);
    this.originalData.tableData[this.type].limitedColumns[clientIndex][
      LcIndex.ppc
    ].value = oldPPP;
    this.originalData.tableData[this.type].limitedColumns[clientIndex][
      LcIndex.ppc
    ].highlight = !(oldPPP === peakPatientPercentage);
  }
 
  private calculateAndUpdateSA(clientPrdIdx : number) {
    const lastClientPrdIdx = this.userDefinedScenarioData.findLastIndex((element) => element.base.IsCompanyProduct === true);
    for (
      let index = lastClientPrdIdx + 1;
      index < this.allColumnsLength;
      index++
    ) {
      this.calculateUpdatedSA(index,clientPrdIdx);
    }
  }
 
  private calculateUpdatedSA(index, clientPrdIdx) {
    const oldSA = +this.originalData.tableData[this.type].allColumns[index][AcIndex.sa].value;
 
    const updatedCompetitorUnits = this.originalData.tableData[this.type].allColumns[index][AcIndex.units].value;
    const baselineCompetitorUnit = this.originalData.finalQuarterData.data[index][FQIndex.unit].value;
 
    const baselineClientUnit = this.originalData.finalQuarterData.data[clientPrdIdx][FQIndex.unit].value;
    const updatedClientUnit = this.originalData.tableData[this.type].allColumns[clientPrdIdx][AcIndex.units].value;
    let updatedSA;
 
    if((updatedClientUnit - baselineClientUnit)!==0){
      updatedSA = ((baselineCompetitorUnit - updatedCompetitorUnits) / (updatedClientUnit - baselineClientUnit)) * 100;
    }else{
      updatedSA=oldSA
    }
   
    this.updateSA(updatedSA, index);
  }
 
  private updateSA(updatedSA, index) {
    const oldSA =
    +this.originalData.tableData[this.type].allColumns[index][AcIndex.sa]
      .value;
    this.originalData.tableData[this.type].allColumns[index][AcIndex.sa].value =
      updatedSA;
    this.originalData.tableData[this.type].allColumns[index][
      AcIndex.sa
    ].highlight = !(oldSA === updatedSA);
    this.originalData.tableData[this.type].limitedColumns[index][
      LcIndex.sa
    ].value = oldSA;
    this.originalData.tableData[this.type].limitedColumns[index][
      LcIndex.sa
    ].highlight = !(oldSA === updatedSA);
    // Persist the updated SA back so getSourceAllocation() reads it on the next calculation
    this.userDefinedData[index][LcIndex.sa] = updatedSA;
  }
}